import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  Fragment,
  useCallback,
} from "react";
import { CloseIcon, DownloadIcon } from "../Svgs";
import { Resizable } from "react-resizable";
import type { ResizeCallbackData } from "react-resizable";
import useUrlParams from "../../../../hooks/useUrlParams";
import "./Console.scss";
import { ConsoleOutputList, Row } from "./ConsoleOutputList";
import { FixedSizeList as List } from "react-window";
import ShimmerMenuList from "../../../../../CommonComponents/ShimmerEffectDesign/ShimmerMenu";
import JsonEditor from "../../../../../CommonComponents/json_editor/JsonEditor";
import useScenario from "../../../../hooks/useScenario";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import { isOperateMode } from "../../../../hooks/utils";
// Read-only rendering: we don't show interactive Pass/Fail buttons in this panel.
import {
  OUTPUT_MODAL_HEIGHT_MIN,
  OUTPUT_MODAL_HEIGHT_MAX,
  OUTPUT_MODAL_WIDTH_MIN,
  OUTPUT_MODAL_WIDTH_MAX,
} from "./constants/consoleConstants";
import { base64ToHex } from "../../../../../../utils/commonFunctions";
import useConsole from "../../../../hooks/useConsole";
import Loader from "../../../../../../Layouts/Loader/Loader";
import { GreenTick, RedWarning, OrangeTimeout } from "../../../../../CommonComponents/svgs/Svgs";

import type {
  ConsoleTableColumn,
  ConsoleEntry,
  SelectedRowState,
  OutputModalSize,
  OutputModalOptions,
  ValidationField,
} from "./types/consoleTypes";
import { flattenValidationRows } from "./validationDisplayFlatten";
import { ValidationParameterLabel } from "./ValidationParameterLabel";
import type { AllFilterType } from "../../../../../CommonComponents/custom_filter/types";

export interface ConsoleOutputSectionProps {
  error: string | null;
  commandDetails: { data?: ConsoleEntry[] } | null;
  getCommandDetails: (commandId: { CommandUid?: string }) => void;
  set_resizing: (resizing: boolean) => void;
  consoleTableColumns: ConsoleTableColumn[];
  isTableWidth: number;
  allFilters: AllFilterType;
  isFrozen: boolean;
  setIsFrozen: (v: boolean) => void;
}

const hasValidationData = (entry: ConsoleEntry | null): boolean => {
  if (!entry) return false;
  const rawDetails = entry.ValidationDetails ?? entry.CommandDetails?.ValidationDetails;
  const rawInfo = entry.ValidationInfo ?? entry.CommandDetails?.ValidationInfo;
  const hasArrayData = (Array.isArray(rawDetails) && rawDetails.length > 0) || (Array.isArray(rawInfo) && rawInfo.length > 0);
  return hasArrayData || entry.result !== undefined;
};

const getDefaultOutputTabState = (
  entry: ConsoleEntry | null,
  isValidateMode: boolean
): OutputModalOptions => {
  const entryType = String(entry?.type ?? entry?.CommandDetails?.tctmType ?? "").trim().toLowerCase();
  const isTmEntry = entryType === "tm";
  const shouldOpenValidations = isValidateMode && isTmEntry && hasValidationData(entry);

  return {
    contents: false,
    json: !shouldOpenValidations,
    bytes: false,
    messages: false,
    validations: shouldOpenValidations,
  };
};

function normalizeValidationInfo(value: unknown): ValidationField[] {
  if (!Array.isArray(value)) return [];

  return value.reduce<ValidationField[]>((acc, item) => {
    if (!item || typeof item !== "object") return acc;
    const entry = item as Record<string, unknown>;
    acc.push({
      Parameter: entry["Parameter"] != null ? String(entry["Parameter"]) : undefined,
      type: entry["type"] != null ? String(entry["type"]) : undefined,
      Validations: entry["Validations"] != null ? String(entry["Validations"]) : undefined,
      ResultValue: entry["ResultValue"] != null ? String(entry["ResultValue"]) : undefined,
      ResultStatus: entry["ResultStatus"] != null ? String(entry["ResultStatus"]) : undefined,
      children: normalizeValidationInfo(entry["children"]),
    });
    return acc;
  }, []);
}

function getValidationStatusTone(status: string | undefined): "pass" | "fail" | "timeout" | "cancelled" {
  const normalizedStatus = String(status ?? "").trim().toLowerCase();
  if (normalizedStatus === "fail") return "fail";
  if (normalizedStatus === "timeout" || normalizedStatus === "timed_out" || normalizedStatus === "timedout") return "timeout";
  if (normalizedStatus === "cancelled" || normalizedStatus === "canceled") return "cancelled";
  if (normalizedStatus === "pass") return "pass";
  return "pass";
}

/** Container for the messages table, output detail modal, and resizable panels */
const ConsoleOutputSection: React.FC<ConsoleOutputSectionProps> = ({
  commandDetails,
  getCommandDetails,
  set_resizing,
  consoleTableColumns,
  isTableWidth,
  allFilters,
  isFrozen,
  setIsFrozen,
}) => {
  const { dashboard } = useUrlParams();
  const listRef = useRef<List>(null);
  const pendingEntryResultsRef = useRef<Map<string, boolean | string>>(new Map());
  const pendingGroupResultsRef = useRef<Map<string, boolean | string>>(new Map());
  const { scenario } = useScenario();
  const { removeCommandUidForHighlight, highlightedCommands, loading } = useConsole();

  const [outputModalSize, setOutputModalSize] = useState<OutputModalSize>({
    width: 500,
    height: window.innerHeight - 234,
    resizing: false,
  });
  const [selectedRow, setSelectedRow] = useState<SelectedRowState>({
    CommandUid: "",
    selectedRow: "",
    commandIndexs: [],
    type: "",
  });
  const [commandType, setCommandType] = useState("");
  const [hexString, setHexString] = useState("");
  const [foundPayload, setFoundPayload] = useState<Record<string, unknown> | "">("");
  const [outputModalOptions, setOutputModalOptions] = useState<OutputModalOptions>({
    contents: false,
    json: true,
    bytes: false,
    messages: false,
    validations: false,
  });
  const [isOutputModalVisible, setIsOutputModalVisible] = useState(false);
  const [entries, setEntries] = useState<ConsoleEntry[]>([]);
  const showCorrespondingTask = true;
  const [showNoMessage, setShowNoMessage] = useState(false);
  const [hasCheckedMessage, setHasCheckedMessage] = useState(false);
  const [dataCount, setDataCount] = useState(0);
  // Validation panel is read-only: no saving state needed.

  /** Test-plan level Pass/Fail map (validate mode only).
   *  Keyed by testPlanId → result set by the operator. */
  const [testPlanResults, setTestPlanResults] = useState<Record<string, boolean | string>>({});

  const scenario_completed = useMemo(
    () => scenario?.status === "SimulationCompleted",
    [scenario?.status]
  );

  const isValidateMode = useMemo(
    () => scenario?.scenarioDeploymentType?.toUpperCase() === "VALIDATE",
    [scenario?.scenarioDeploymentType]
  );

  // 36px accounts for the .content__container__table__header height
  // so the react-window list exactly fills the remaining container space without causing overflow.
  const listDisplayHeight = isOutputModalVisible
    ? Math.max(150, Math.floor(window.innerHeight * 0.6325) - 280) - 36
    : outputModalSize.height - 36;

  const updateHexAndPayload = useCallback(
    (selected_command: ConsoleEntry | number) => {
      let base64String = "";
      let payload: Record<string, unknown> = {};
      if (commandDetails?.data) {
        const command = commandDetails.data.find((c) => c.type === commandType);
        if (command) {
          base64String = command.CommandDetails?.completeFrame ?? "";
          payload = { ...(command.CommandDetails ?? {}) };
        }
      }
      delete payload.payloadBytes;
      delete payload.completeFrame;
      setHexString(base64ToHex(base64String));
      setFoundPayload(payload);
      setIsOutputModalVisible(true);
    },
    [commandDetails, commandType]
  );

  useEffect(() => {
    if (commandType && commandDetails) {
      updateHexAndPayload(selectedRow.selectedRow as unknown as ConsoleEntry);
    }
  }, [commandType, commandDetails]);

  useEffect(() => {
    const clearFormFields = () => {
      setSelectedRow({ CommandUid: "", selectedRow: "", commandIndexs: [], type: "" });
      setHexString("");
      setFoundPayload("");
      setCommandType("");
      setOutputModalOptions(getDefaultOutputTabState(null, isValidateMode));
      setIsOutputModalVisible(false);
    };
    window.addEventListener("clearFormFields", clearFormFields);
    return () => window.removeEventListener("clearFormFields", clearFormFields);
  }, [isValidateMode]);

  useEffect(() => {
    const handler = () => setIsFrozen(false);
    window.addEventListener("openConsole", handler);
    return () => window.removeEventListener("openConsole", handler);
  }, [setIsFrozen]);

  useEffect(() => {
    const updateHeight = () => {
      setOutputModalSize((prev) => ({ ...prev, height: window.innerHeight - 234 }));
    };
    window.addEventListener("resize", updateHeight);
    updateHeight();
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const show_selected_output_details = useCallback(
    (selected_command: ConsoleEntry | null, entriesList: ConsoleEntry[]) => {
      if (selected_command) {
        const { type, CommandUid, uniqueId } = selected_command;
        setCommandType(type ?? "");
        const positions = entriesList?.reduce<number[]>((acc, output, index) => {
          if (output.CommandUid === CommandUid) acc.push(index);
          return acc;
        }, []);
        const selected_row = entriesList.indexOf(selected_command);
        const already_selected = selected_row === selectedRow.selectedRow;
        setSelectedRow({
          CommandUid: already_selected ? "" : (uniqueId ?? ""),
          selectedRow: already_selected ? "" : selected_row,
          commandIndexs: already_selected ? [] : positions,
          type: already_selected ? "" : (type ?? ""),
        });
        if (!already_selected) {
          setOutputModalOptions(getDefaultOutputTabState(selected_command, isValidateMode));
          updateHexAndPayload(selected_command);
        } else {
          setHexString("");
          setFoundPayload("");
          setCommandType("");
          setOutputModalOptions(getDefaultOutputTabState(null, isValidateMode));
          setIsOutputModalVisible(false);
          setIsFrozen(false);
        }
      } else {
        setHexString("");
        setFoundPayload("");
        setCommandType("");
        setSelectedRow({ CommandUid: "", selectedRow: "", commandIndexs: [], type: "" });
        setOutputModalOptions(getDefaultOutputTabState(null, isValidateMode));
      }
    },
    [isValidateMode, selectedRow.selectedRow, updateHexAndPayload]
  );

  const onResizeHeight = useCallback(
    (_e: React.SyntheticEvent, { size }: { node: HTMLElement; size: { height: number }; handle: string }) => {
      _e.stopPropagation();
      if (size.height >= OUTPUT_MODAL_HEIGHT_MIN && size.height <= OUTPUT_MODAL_HEIGHT_MAX) {
        setOutputModalSize((prev) => ({ ...prev, height: size.height, resizing: "height" }));
      }
    },
    []
  );

  const onResizeWidth = useCallback(
    (_e: React.SyntheticEvent, { size }: { node: HTMLElement; size: { width: number }; handle: string }) => {
      _e.stopPropagation();
      if (size.width >= OUTPUT_MODAL_WIDTH_MIN && size.width <= OUTPUT_MODAL_WIDTH_MAX) {
        setOutputModalSize((prev) => ({ ...prev, width: size.width, resizing: "width" }));
      }
    },
    []
  );

  const onResizeStop = useCallback(
    (_e: React.SyntheticEvent, _data: ResizeCallbackData) => {
      _e.stopPropagation();
      set_resizing(false);
      setOutputModalSize((prev) => ({ ...prev, resizing: false }));
    },
    [set_resizing]
  );

  const onResizeStart = useCallback(
    (_e: React.SyntheticEvent, _data: ResizeCallbackData) => {
      _e.stopPropagation();
      set_resizing(true);
    },
    [set_resizing]
  );

  const handleTabChange = useCallback((selectedKey: keyof OutputModalOptions) => {
    setOutputModalOptions({
      contents: false,
      json: false,
      bytes: false,
      messages: false,
      validations: false,
      [selectedKey]: true,
    });
  }, []);

  useEffect(() => {
    if (commandDetails && selectedRow) {
      const correspondingCommand = commandDetails?.data?.find(
        (entry) => entry?.type !== selectedRow?.type
      );
      setShowNoMessage(!correspondingCommand);
      setHasCheckedMessage(true);
    }
  }, [commandDetails, selectedRow]);

  const filteredColumns = useMemo(() => {
    // Validate+Summary me "operate" jaisi columns show honi chahiye (running-session parity).
    const showOperateColumns = dashboard === "operate" || isValidateMode;
    return showOperateColumns
      ? consoleTableColumns.filter((item) => item.isSelected)
      : consoleTableColumns.filter((item) => item.isSelected && item.dashboard !== "operate");
  }, [consoleTableColumns, dashboard, isValidateMode]);

  const visibleColumns = useMemo(
    () => filteredColumns,
    [filteredColumns],
  );

  const selectedEntry = useMemo(() => {
    if (typeof selectedRow.selectedRow !== "number") return null;
    return entries[selectedRow.selectedRow] ?? null;
  }, [entries, selectedRow.selectedRow]);

  const selectedDetailedEntry = useMemo(() => {
    if (!selectedEntry) return null;

    return (
      commandDetails?.data?.find((entry) => entry?.type === (selectedEntry.type ?? commandType))
      ?? selectedEntry
    );
  }, [commandDetails?.data, commandType, selectedEntry]);

  const validationRows = useMemo((): ValidationField[] => {
    const fromApi = normalizeValidationInfo(
      selectedDetailedEntry?.ValidationDetails
      ?? selectedDetailedEntry?.CommandDetails?.ValidationDetails
      ?? selectedDetailedEntry?.ValidationInfo
      ?? selectedDetailedEntry?.CommandDetails?.ValidationInfo
    );
    return fromApi;
  }, [selectedDetailedEntry]);

  const validationDisplayRows = useMemo(
    () => flattenValidationRows(validationRows),
    [validationRows],
  );

  const selectedValidationResult = useMemo(() => {
    if (selectedEntry?.result === undefined) return undefined;
    return Boolean(selectedEntry.result);
  }, [selectedEntry]);

  // Interactive validation update removed: this panel now displays backend results only.

  const itemData = useMemo(
    () => ({
      entries,
      selectedRow,
      getCommandDetails,
      show_selected_output_details,
      consoleTableColumns,
      setIsOutputModalVisible,
      isOutputModalVisible,
      commandDetails,
      showCorrespondingTask,
      setIsFrozen,
      setDataCount,
      listRef,
      height: listDisplayHeight,
      isFrozen,
      frozenData: null as ConsoleEntry[] | null,
      removeCommandUidForHighlight,
      highlightedCommands,
      dashboard,
      filteredColumns: visibleColumns,
      // validate-mode fields — not used in the Messages tab fallback List
      testPlanResults,
      setTestPlanResults,
      setEntries,
      isValidateMode: false,
    }),
    [
      entries, selectedRow, getCommandDetails, show_selected_output_details, consoleTableColumns,
      commandDetails, showCorrespondingTask, setIsFrozen, setDataCount, listDisplayHeight, isFrozen,
      removeCommandUidForHighlight, highlightedCommands, dashboard, visibleColumns,
      testPlanResults, setTestPlanResults, setEntries,
    ]
  );

  /** Full itemData for Messages tab List */
  const messageTabItemData = useMemo(
    () => ({
      entries: commandDetails?.data ?? [],
      selectedRow,
      getCommandDetails,
      show_selected_output_details,
      consoleTableColumns,
      setIsOutputModalVisible,
      isOutputModalVisible,
      commandDetails,
      showCorrespondingTask: true,
      setIsFrozen,
      setDataCount,
      listRef,
      height: 35,
      isFrozen: false,
      frozenData: null as ConsoleEntry[] | null,
      removeCommandUidForHighlight,
      highlightedCommands,
      dashboard,
      filteredColumns: visibleColumns,
      testPlanResults,
      setTestPlanResults,
      setEntries,
      isValidateMode: false,
      readOnlyResults: dashboard === "summary",
      pendingEntryResultsRef,
      pendingGroupResultsRef,
    }),
    [
      commandDetails, selectedRow, getCommandDetails, show_selected_output_details,
      consoleTableColumns, setIsOutputModalVisible, isOutputModalVisible, setIsFrozen, setDataCount,
      removeCommandUidForHighlight, highlightedCommands, dashboard, visibleColumns,
      testPlanResults, setTestPlanResults, setEntries,
    ]
  );

  const removeSelection = useCallback(() => {
    setIsOutputModalVisible(false);
    setSelectedRow({ CommandUid: "", selectedRow: "", commandIndexs: [], type: "" });
    setOutputModalOptions(getDefaultOutputTabState(null, isValidateMode));
    setIsFrozen(false);
  }, [isValidateMode, setIsFrozen]);

  const downloadOutputDetails = useCallback(() => {
    if (!hexString || !foundPayload) return;
    const data = { "Packet Details": foundPayload, "Raw Bytes": hexString };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const element = document.createElement("a");
    element.href = URL.createObjectURL(blob);
    element.download = `output_details_${selectedRow?.CommandUid ?? "data"}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(element.href);
  }, [hexString, foundPayload, selectedRow?.CommandUid]);

  useEffect(() => {
    const handleRemoveSelection = () => removeSelection();
    window.addEventListener("removeSelection", handleRemoveSelection);
    return () => window.removeEventListener("removeSelection", handleRemoveSelection);
  }, [removeSelection]);

  const headerRef = useRef<HTMLDivElement>(null);
  const listOuterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerEl = headerRef.current;
    const outerEl = listOuterRef.current;
    if (!headerEl || !outerEl) return;

    let isSyncingOuter = false;
    let isSyncingHeader = false;

    const handleOuterScroll = () => {
      if (isSyncingHeader) {
        isSyncingHeader = false;
        return;
      }
      isSyncingOuter = true;
      headerEl.scrollLeft = outerEl.scrollLeft;
    };

    const handleHeaderScroll = () => {
      if (isSyncingOuter) {
        isSyncingOuter = false;
        return;
      }
      isSyncingHeader = true;
      outerEl.scrollLeft = headerEl.scrollLeft;
    };

    outerEl.addEventListener("scroll", handleOuterScroll, { passive: true });
    headerEl.addEventListener("scroll", handleHeaderScroll, { passive: true });

    return () => {
      outerEl.removeEventListener("scroll", handleOuterScroll);
      headerEl.removeEventListener("scroll", handleHeaderScroll);
    };
  }, []);

  return (
    <>
      <div
        style={{
          width: "100%",
          height: isOutputModalVisible
            ? "calc(100vh - 115px - 36.75vh - 130px)"
            : "calc(100vh - 180px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        className={`right__panel__body__content__container ${isOutputModalVisible ? "outputModalVisibleShortHeight" : ""
          } right__panel__table__width`}
      >
        <div
          ref={headerRef}
          className="content__container__table__header__wrapper"
        >
          <div
            className="content__container__table__header right__panel__table__width"
          >
            {visibleColumns.map((item) => {
              const widthStr = String(item.width ?? "");
              const isIconCol = item.label === "TcTmType";
              const parsedWidth = widthStr.includes("px") || widthStr === "flex" || widthStr === "100%"
                ? (isIconCol ? "50px" : widthStr)
                : `${widthStr}px`;

              const minW = isIconCol
                ? "50px"
                : (parsedWidth !== "flex" && parsedWidth !== "100%" && parsedWidth !== "flexpx" && parsedWidth !== "100%px"
                  ? parsedWidth
                  : "100px");

              const cellStyle: React.CSSProperties = isIconCol
                ? { width: "50px", minWidth: "50px", flexShrink: 0, padding: "2px 4px" }
                : {
                  flex: "1 1 0px",
                  minWidth: minW,
                  width: minW,
                  overflow: "hidden",
                  padding: "2px 8px",
                  boxSizing: "border-box",
                };
              const align = isIconCol ? "center" : "start";

              return (
                <div
                  key={item.label}
                  className={`d-flex align-items-center justify-content-${align}`}
                  style={cellStyle}
                >
                  <span className="truncate__text">{item?.isSvg ? item.svg : item?.label}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="position-relative right__panel__table__width"
          style={{
            width: "100%",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Resizable
            height={listDisplayHeight}
            axis="y"
            onResizeStop={onResizeStop}
            onResizeStart={onResizeStart}
            onResize={onResizeHeight}
            handle={(handleAxis, ref) => (
              <div
                ref={ref}
                className={`container__table__body__resize__handler ${outputModalSize.resizing === "height" ? "active" : ""
                  } handle-${handleAxis}`}
              />
            )}
          >
            <div style={{ height: listDisplayHeight }}>
              <div className="content__container__table__body">
                <ConsoleOutputList
                  height={listDisplayHeight}
                  getCommandDetails={getCommandDetails}
                  show_selected_output_details={show_selected_output_details}
                  selectedRow={selectedRow}
                  consoleTableColumns={consoleTableColumns}
                  isOutputModalVisible={isOutputModalVisible}
                  setIsOutputModalVisible={setIsOutputModalVisible}
                  allFilters={allFilters}
                  setEntries={setEntries}
                  entries={entries}
                  setIsFrozen={setIsFrozen}
                  isFrozen={isFrozen}
                  dataCount={dataCount}
                  setDataCount={setDataCount}
                  setSelectedRow={setSelectedRow}
                  testPlanResults={testPlanResults}
                  setTestPlanResults={setTestPlanResults}
                  filteredColumns={visibleColumns}
                  outerRef={listOuterRef}
                />
              </div>
            </div>
          </Resizable>
        </div>
      </div>

      {isOutputModalVisible && (
        <div className="output__modal">
          <div className="output__modal__header__console">
            <div className="output__modal__header__console__options__container">
              <button
                type="button"
                className={`output__modal__header__console__option ${outputModalOptions.json ? "active" : ""}`}
                onClick={() => handleTabChange("json")}
              >
                CONTENTS
              </button>
              <button
                type="button"
                className={`output__modal__header__console__option ${outputModalOptions.messages ? "active" : ""}`}
                onClick={() => handleTabChange("messages")}
              >
                MESSAGES
              </button>
              {isValidateMode && (
                <button
                  type="button"
                  className={`output__modal__header__console__option ${outputModalOptions.validations ? "active" : ""}`}
                  onClick={() => handleTabChange("validations")}
                >
                  VALIDATIONS
                </button>
              )}
            </div>
            <div className="output__modal__header__console__actions_btns">
              {hexString && foundPayload && (
                <CustomToolTip title="Download Output" placement="top">
                  <button type="button" onClick={downloadOutputDetails}>
                    <DownloadIcon />
                  </button>
                </CustomToolTip>
              )}
              <button type="button" onClick={removeSelection}>
                <CloseIcon />
              </button>
            </div>
          </div>

          <div
            className={`right__panel__body__content__container ${isOutputModalVisible ? "outputModalVisibleShortHeight" : ""
              } right__panel__table__width modal__scrollbar__white__horizontal`}
          >
            <div className="output__modal__body__container">
              {outputModalOptions?.json && (
                <Fragment>
                  <Resizable
                    className="position-relative"
                    width={outputModalSize.width}
                    height={outputModalSize.height}
                    axis="x"
                    onResizeStop={onResizeStop}
                    onResizeStart={onResizeStart}
                    onResize={onResizeWidth}
                    handle={(handleAxis, ref) => (
                      <div
                        ref={ref}
                        className={`output__modal__left__resize__handler ${outputModalSize.resizing === "width" ? "active" : ""
                          } handle-${handleAxis}`}
                      />
                    )}
                  >
                    <div>
                      <div
                        className="output__modal__left__section"
                        style={{ width: outputModalSize.width }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <span>Packet Details:</span>
                        {loading ? (
                          <Loader />
                        ) : foundPayload ? (
                          <JsonEditor
                            value={JSON.stringify({ payload: foundPayload }, null, 2)}
                            editable={false}
                            backgroundColor="transparent"
                          />
                        ) : (
                          "Not available"
                        )}
                      </div>
                    </div>
                  </Resizable>

                  <div
                    className="output__modal__right__section"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <span>Raw Bytes:</span>
                    <div
                      onMouseDown={(e) => e.stopPropagation()}
                      {...({
                        onSelectStart: (e: React.SyntheticEvent) => e.stopPropagation(),
                      } as React.HTMLAttributes<HTMLDivElement>)}
                    >
                      {loading ? <Loader /> : hexString ? hexString : "Not available"}
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
            {!hasCheckedMessage && <ShimmerMenuList />}
            {outputModalOptions?.messages &&
              hasCheckedMessage &&
              (showNoMessage ? (
                <div className="output__div__message__container">No associated message found.</div>
              ) : (
                <div
                  style={{
                    minWidth: dashboard === "operate" ? `${isTableWidth}px` : "auto",
                    marginTop: "8px",
                  }}
                  className="right__panel__body__content__container right__panel__table__width modal__scrollbar__white__horizontal"
                >
                  <List
                    ref={listRef}
                    itemData={messageTabItemData}
                    height={35}
                    itemCount={1}
                    itemSize={35}
                    width="100%"
                  >
                    {Row}
                  </List>
                </div>
              ))}
            {isValidateMode && outputModalOptions?.validations && (
              <div className="output__validation__tab">
                <div className="output__validation__tab__header">
                  {/* <span className="output__validation__tab__title">Output Validation</span> */}
                  {/* read-only: no saving badge */}
                </div>

                {validationDisplayRows.length > 0 ? (
                  <div className="output__validation__table">
                    <div className="output__validation__table__header">
                      <span>Parameters</span>
                      <span>Validations</span>
                      <span>Actual value</span>
                      <span>Results</span>
                    </div>
                    <div className="output__validation__table__body">
                      {validationDisplayRows.map((row) => {
                        const statusTone = getValidationStatusTone(row.resultStatus);
                        const actualText =
                          row.resultValue != null && String(row.resultValue).trim() !== ""
                            ? row.resultValue
                            : "—";
                        const isFail = row.resultStatus?.toLowerCase().trim() === "fail";
                        const isPass = row.resultStatus?.toLowerCase().trim() === "pass";
                        const isTimeout =
                          row.resultStatus?.toLowerCase().trim() === "timeout" ||
                          row.resultStatus?.toLowerCase().trim() === "timed_out" ||
                          row.resultStatus?.toLowerCase().trim() === "timedout";
                        const rowTypeClass = isFail
                          ? "output__validation__table__row--fail"
                          : isPass
                          ? "output__validation__table__row--pass"
                          : isTimeout
                          ? "output__validation__table__row--timeout"
                          : "";
                        const depthClass = row.depth === 0 ? "output__validation__table__row--root" : "";

                        return (
                          <div
                            key={row.key}
                            className={`output__validation__table__row ${rowTypeClass} ${depthClass} rp__validation-table__row`}
                            data-depth={row.depth}
                          >
                            <span className="output__validation__table__cell output__validation__table__cell--parameter">
                              <ValidationParameterLabel
                                depth={row.depth}
                                label={row.parameter}
                                nodeName={row.nodeName}
                                nodeType={row.nodeType}
                                fullPath={row.fullPath}
                                hasChildren={row.hasChildren}
                                isLastChild={row.isLastChild}
                                ancestorRails={row.ancestorRails}
                                hasChildFailure={row.hasChildFailure}
                                status={row.resultStatus}
                              />
                            </span>
                            <span className="output__validation__table__cell">
                              {row.validations ?? ""}
                            </span>
                            <span
                              className={`output__validation__table__cell output__validation__table__cell--actual output__validation__table__cell--type-${row.valueType}`}
                            >
                              {(() => {
                                if (row.valueType === "number" && actualText !== "—") {
                                  const parts = actualText.match(/^([-\d.]+)\s*(.*)$/);
                                  if (parts) {
                                    return (
                                      <>
                                        <span className="output__validation__value-num">{parts[1]}</span>
                                        {parts[2] && <span className="output__validation__value-unit"> {parts[2]}</span>}
                                      </>
                                    );
                                  }
                                }
                                return actualText;
                              })()}
                            </span>
                            <span className="output__validation__table__cell output__validation__table__cell--result">
                              {row.hasChildren && row.statusSummary ? (
                                <span className="output__validation__status-summary">
                                  {row.statusSummary}
                                </span>
                              ) : row.resultStatus ? (
                                <span className={`output__validation__status output__validation__status--${statusTone}`}>
                                  {statusTone === "pass" ? <GreenTick /> : statusTone === "fail" ? <RedWarning /> : statusTone === "timeout" ? <OrangeTimeout /> : null}
                                  {row.resultStatus}
                                </span>
                              ) : (
                                <span className="output__validation__table__result-value">—</span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="output__div__message__container">No data found.</div>
                )}

              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ConsoleOutputSection;
